import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAssistantContext } from "../contexts/AssistantContext";
import { chapters, costEstimatorDefaults } from "../data/chapters";
import CostEstimator from "../components/CostEstimator";
import Navbar from "../components/Navbar";
import ChapterView from "../components/ebook/ChapterView";
import StepZeroView from "../components/ebook/StepZeroView";
import UpgradeModal from "../components/ebook/UpgradeModal";
import CelebrationModal from "../components/ebook/CelebrationModal";
import ExitIntentModal from "../components/ebook/ExitIntentModal";
import TocSidebar from "../components/ebook/TocSidebar";
import { generatePdf } from "../utils/generatePdf";
import confetti from "canvas-confetti";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  BookOpen,
  FileText,
  UserCheck,
  FilePlus,
  Hash,
  Landmark,
  Receipt,
  Shield,
  CalendarCheck,
  Calculator,
  Lock,
  ListChecks,
} from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";
import { log } from "../utils/logger";
import { PENDING_ESTIMATE_KEY } from "../components/landing/HeroCostCalculator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  FileText, UserCheck, FilePlus, BookOpen, Hash, Landmark, Receipt, Shield, CalendarCheck, Calculator, ListChecks,
};

const pageVariants = {
  enter: (dir) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0 }),
};

// Search helper: check if a chapter matches a query
function chapterMatchesQuery(ch, query) {
  const q = query.toLowerCase();
  if (ch.title.toLowerCase().includes(q)) return true;
  if (ch.subtitle.toLowerCase().includes(q)) return true;
  if (ch.content.intro.toLowerCase().includes(q)) return true;
  if (ch.content.proTip.toLowerCase().includes(q)) return true;
  for (const s of ch.content.sections) {
    if (s.heading.toLowerCase().includes(q)) return true;
    for (const item of s.items) {
      if (item.toLowerCase().includes(q)) return true;
    }
  }
  for (const item of ch.checklistItems) {
    if (item.toLowerCase().includes(q)) return true;
  }
  return false;
}

export default function EbookPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(() => location.state?.page ?? 0);
  const [direction, setDirection] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [costValues, setCostValues] = useState({ ...costEstimatorDefaults });
  const [bookmarkedChapters, setBookmarkedChapters] = useState([]);
  const [notes, setNotes] = useState({});
  const [step0Answers, setStep0Answers] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCelebration, setShowCelebration] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const exitIntentShown = useRef(false);

  const totalPages = chapters.length + 1;
  const { setContext: setAssistantContext } = useAssistantContext();

  useEffect(() => {
    const ch = chapters[currentPage];
    setAssistantContext(
      ch
        ? { key: ch.id, label: `Step ${ch.id}: ${ch.title}` }
        : { key: "cost", label: "Startup Cost Estimator" }
    );
    return () => setAssistantContext(null);
  }, [currentPage, setAssistantContext]);

  // Handle initial page from quickstart navigation
  useEffect(() => {
    if (location.state?.page != null) {
      setCurrentPage(location.state.page);
    }
  }, [location.state]);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  // Load referral code (used to deep-link milestone shares back to this user's referral link)
  useEffect(() => {
    if (!user) return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/referrals/me`, { withCredentials: true });
        if (!cancel) setReferralCode(data.referral_code);
      } catch (err) {
        log.warn("Referral code load failed", err);
      }
    })();
    return () => { cancel = true; };
  }, [user]);

  const loadProgress = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/progress`, { withCredentials: true });
      if (data.completed_steps) setCompletedSteps(data.completed_steps);
      if (data.cost_estimator && Object.keys(data.cost_estimator).length > 0) {
        setCostValues(data.cost_estimator);
        localStorage.removeItem(PENDING_ESTIMATE_KEY);
      } else {
        const pending = localStorage.getItem(PENDING_ESTIMATE_KEY);
        if (pending) {
          try {
            const est = { ...costEstimatorDefaults, ...JSON.parse(pending) };
            setCostValues(est);
            await axios.put(`${API}/progress`, {
              completed_steps: data.completed_steps || [],
              cost_estimator: est,
              bookmarked_chapters: data.bookmarked_chapters || [],
              notes: data.notes || {},
              step0_answers: data.step0_answers || {},
            }, { withCredentials: true });
            toast.success("Your cost estimate from the homepage was saved to the Cost Estimator.");
          } catch (err) {
            log.warn("Bad pending estimate", err);
          }
          localStorage.removeItem(PENDING_ESTIMATE_KEY);
        }
      }
      if (data.bookmarked_chapters) setBookmarkedChapters(data.bookmarked_chapters);
      if (data.notes) setNotes(data.notes);
      if (data.step0_answers) setStep0Answers(data.step0_answers);
    } catch (err) {
      log.warn("Failed to load progress", err);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadProgress();
  }, [user, loadProgress]);

  const saveProgress = useCallback(async (steps, costs, bookmarks, n, s0) => {
    try {
      await axios.put(`${API}/progress`, {
        completed_steps: steps,
        cost_estimator: costs,
        bookmarked_chapters: bookmarks,
        notes: n,
        step0_answers: s0 ?? step0Answers,
      }, { withCredentials: true });
    } catch (err) {
      log.warn("Failed to save progress", err);
    }
  }, [step0Answers]);

  const handleStep0AnswerChange = useCallback((next) => {
    setStep0Answers(next);
    saveProgress(completedSteps, costValues, bookmarkedChapters, notes, next);
  }, [completedSteps, costValues, bookmarkedChapters, notes, saveProgress]);

  // Exit-intent detection for non-premium users
  useEffect(() => {
    const isPrem = user?.is_premium || user?.role === "admin";
    if (isPrem) return;
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !exitIntentShown.current) {
        exitIntentShown.current = true;
        setShowExitIntent(true);
      }
    };
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [user]);

  // Build checklist index (memoized, immutable reduce)
  const chapterChecklistStart = useMemo(() =>
    chapters.map((_, i) =>
      chapters.slice(0, i).reduce((sum, c) => sum + c.checklistItems.length, 0)
    ),
  []);

  // Check if a chapter's checklist is fully complete
  const isChapterComplete = useCallback((chapterIdx) => {
    if (chapterIdx < 0 || chapterIdx >= chapters.length) return false;
    const ch = chapters[chapterIdx];
    const start = chapterChecklistStart[chapterIdx];
    return ch.checklistItems.every((_, j) => completedSteps.includes(start + j));
  }, [completedSteps, chapterChecklistStart]);

  // Premium paywall: Steps 3-9 (chapter ids 3-9) require premium. With Step 0 inserted at index 0,
  // Step 3 is now at array index 3.
  const isPremium = user?.is_premium || user?.role === "admin";
  const requiresPremium = useCallback((page) => page >= 3 && !isPremium, [isPremium]);

  // A page is unlocked if all previous chapters are complete AND premium check passes
  const isPageUnlocked = useCallback((page) => {
    if (page === 0) return true;
    if (requiresPremium(page)) return false;
    for (let i = 0; i < page && i < chapters.length; i++) {
      if (!isChapterComplete(i)) return false;
    }
    return true;
  }, [requiresPremium, isChapterComplete]);

  const handleUpgrade = useCallback(async (discountCode) => {
    setCheckoutLoading(true);
    try {
      const origin = window.location.origin;
      const payload = { origin_url: origin };
      if (discountCode) payload.discount_code = discountCode;
      const { data } = await axios.post(`${API}/payments/checkout`, payload, { withCredentials: true });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start checkout");
      setCheckoutLoading(false);
    }
  }, []);

  const goToPage = useCallback((page) => {
    if (requiresPremium(page)) {
      setShowUpgrade(true);
      return;
    }
    if (!isPageUnlocked(page)) {
      toast.error("Complete the current step first!", { duration: 2000 });
      return;
    }
    setDirection(page > currentPage ? 1 : -1);
    setCurrentPage(page);
    setSidebarOpen(false);
    setSearchQuery("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [requiresPremium, isPageUnlocked, currentPage]);

  const nextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      if (requiresPremium(currentPage + 1)) {
        setShowUpgrade(true);
        return;
      }
      if (!isPageUnlocked(currentPage + 1)) {
        toast.error("Complete all checklist items to proceed!", { duration: 2000 });
        return;
      }
      setDirection(1);
      setCurrentPage((p) => p + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentPage, totalPages, requiresPremium, isPageUnlocked]);

  const prevPage = useCallback(() => {
    if (currentPage > 0) {
      setDirection(-1);
      setCurrentPage((p) => p - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentPage]);

  const toggleStep = useCallback((stepIndex) => {
    setCompletedSteps((prev) => {
      const next = prev.includes(stepIndex)
        ? prev.filter((s) => s !== stepIndex)
        : [...prev, stepIndex];
      saveProgress(next, costValues, bookmarkedChapters, notes);
      if (!prev.includes(stepIndex)) {
        toast.success("Step completed!", { duration: 2000 });

        // Check if current chapter is now fully complete
        const ch = chapters[currentPage];
        if (ch) {
          const start = chapterChecklistStart[currentPage];
          const chapterDone = ch.checklistItems.every((_, j) => next.includes(start + j));
          if (chapterDone) {
            confetti({
              particleCount: 80,
              spread: 70,
              origin: { y: 0.6 },
              colors: ["#1E3A8A", "#A3E6D5", "#FDFCF8", "#1A1918"],
            });
          }
        }

        // Check if ALL steps are now complete
        const totalItems = chapters.reduce((a, c) => a + c.checklistItems.length, 0);
        if (next.length === totalItems) {
          setTimeout(() => {
            confetti({
              particleCount: 200,
              spread: 120,
              origin: { y: 0.4 },
              colors: ["#1E3A8A", "#A3E6D5", "#FFD700", "#FDFCF8"],
            });
            setShowCelebration(true);
          }, 500);
        }
      }
      return next;
    });
  }, [costValues, bookmarkedChapters, notes, currentPage, chapterChecklistStart, saveProgress]);

  const toggleBookmark = useCallback((chapterId) => {
    setBookmarkedChapters((prev) => {
      const next = prev.includes(chapterId)
        ? prev.filter((id) => id !== chapterId)
        : [...prev, chapterId];
      saveProgress(completedSteps, costValues, next, notes);
      toast(prev.includes(chapterId) ? "Bookmark removed" : "Chapter bookmarked!", { duration: 1500 });
      return next;
    });
  }, [completedSteps, costValues, notes, saveProgress]);

  const handleCostChange = useCallback((newValues) => {
    setCostValues(newValues);
    saveProgress(completedSteps, newValues, bookmarkedChapters, notes);
  }, [completedSteps, bookmarkedChapters, notes, saveProgress]);

  const handleNoteSave = useCallback((chapterId, text) => {
    const updated = { ...notes, [String(chapterId)]: text };
    setNotes(updated);
    saveProgress(completedSteps, costValues, bookmarkedChapters, updated);
    toast.success("Note saved!", { duration: 1500 });
  }, [notes, completedSteps, costValues, bookmarkedChapters, saveProgress]);

  const handleExportPdf = useCallback(() => {
    generatePdf(completedSteps, costValues, user?.name || user?.email || "");
    toast.success("PDF downloaded!", { duration: 2000 });
  }, [completedSteps, costValues, user]);

  const totalChecklist = chapters.reduce((a, c) => a + c.checklistItems.length, 0);
  const completedTotal = completedSteps.length;
  const progressPercent = totalChecklist > 0 ? Math.round((completedTotal / totalChecklist) * 100) : 0;
  const allComplete = completedTotal === totalChecklist && totalChecklist > 0;

  // Filter chapters by search
  const filteredChapters = searchQuery.trim()
    ? chapters.filter((ch) => chapterMatchesQuery(ch, searchQuery.trim()))
    : chapters;

  if (loading || !user) return null;

  const isCostPage = currentPage === chapters.length;
  const chapter = isCostPage ? null : chapters[currentPage];
  const ChapterIcon = chapter ? (iconMap[chapter.icon] || FileText) : Calculator;

  const renderPage = () => {
    if (isCostPage) return <CostEstimator values={costValues} onChange={handleCostChange} />;
    if (chapter?.isPrerequisite) {
      return (
        <StepZeroView
          chapter={chapter}
          completedSteps={completedSteps}
          checklistStart={chapterChecklistStart[currentPage]}
          onToggle={toggleStep}
          answers={step0Answers}
          onAnswerChange={handleStep0AnswerChange}
        />
      );
    }
    return (
      <ChapterView
        chapter={chapter}
        ChapterIcon={ChapterIcon}
        completedSteps={completedSteps}
        checklistStart={chapterChecklistStart[currentPage]}
        onToggle={toggleStep}
        isBookmarked={bookmarkedChapters.includes(chapter?.id)}
        onToggleBookmark={() => toggleBookmark(chapter?.id)}
        noteText={notes[String(chapter?.id)] || ""}
        onNoteSave={(text) => handleNoteSave(chapter?.id, text)}
        referralCode={referralCode}
      />
    );
  };

  return (
    <div className="flex min-h-screen flex-col bg-[var(--c-bg)]">
      <Navbar />

      <div className="mx-auto flex w-full max-w-[1600px] flex-1 gap-0 px-4 py-6 lg:gap-6 lg:px-8">
        {/* Mobile sidebar toggle */}
        <button
          data-testid="mobile-sidebar-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="fixed right-4 top-20 z-40 flex h-14 w-14 items-center justify-center rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] text-white shadow-[4px_4px_0px_var(--c-shadow)] lg:hidden"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>

        <TocSidebar
          sidebarOpen={sidebarOpen}
          user={user}
          progressPercent={progressPercent}
          completedTotal={completedTotal}
          totalChecklist={totalChecklist}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filteredChapters={filteredChapters}
          chapterChecklistStart={chapterChecklistStart}
          completedSteps={completedSteps}
          bookmarkedChapters={bookmarkedChapters}
          notes={notes}
          currentPage={currentPage}
          isCostPage={isCostPage}
          isPageUnlocked={isPageUnlocked}
          requiresPremium={requiresPremium}
          goToPage={goToPage}
          handleExportPdf={handleExportPdf}
          allComplete={allComplete}
        />

        {/* Overlay for mobile sidebar */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-20 bg-black/30 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Main Book Area */}
        <main className="flex flex-1 flex-col">
          <div
            data-testid="book-page"
            className="relative flex-1 overflow-hidden rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] shadow-[6px_6px_0px_var(--c-shadow)]"
          >
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentPage}
                custom={direction}
                variants={pageVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                className="h-full"
              >
                <ScrollArea className="h-[calc(100vh-180px)]">
                  <div className="p-6 pb-24 sm:p-8 lg:p-10">
                    {renderPage()}
                  </div>
                </ScrollArea>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div data-testid="page-navigation" className="mt-4 flex items-center justify-between">
            <button
              data-testid="prev-page-button"
              onClick={prevPage}
              disabled={currentPage === 0}
              className="flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-5 py-2.5 font-bold text-[var(--c-text)] shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_var(--c-shadow)] disabled:opacity-40 disabled:hover:translate-y-0"
            >
              <ChevronLeft className="h-5 w-5" />
              <span className="hidden sm:inline">Previous</span>
            </button>
            <span className="text-sm font-semibold text-[var(--c-muted)]">{currentPage + 1} / {totalPages}</span>
            <button
              data-testid="next-page-button"
              onClick={nextPage}
              disabled={currentPage === totalPages - 1}
              className={`flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] px-5 py-2.5 font-bold shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_var(--c-shadow)] disabled:opacity-40 disabled:hover:translate-y-0 ${
                !isCostPage && !isChapterComplete(currentPage)
                  ? "bg-[var(--c-surface)] text-[var(--c-muted)]"
                  : "bg-[#1E3A8A] text-white"
              }`}
            >
              {!isCostPage && !isChapterComplete(currentPage) && <Lock className="h-4 w-4" />}
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </main>
      </div>

      {showUpgrade && (
        <UpgradeModal
          onClose={() => setShowUpgrade(false)}
          onUpgrade={handleUpgrade}
          checkoutLoading={checkoutLoading}
        />
      )}

      {showCelebration && (
        <CelebrationModal
          onClose={() => setShowCelebration(false)}
          user={user}
        />
      )}

      {showExitIntent && (
        <ExitIntentModal
          onClose={() => setShowExitIntent(false)}
          onUpgrade={() => { setShowExitIntent(false); setShowUpgrade(true); }}
        />
      )}
    </div>
  );
}

